import { useWebRTC } from "providers/WebRTCProvider";

import FunctionalButton from "components/layout/FunctionalButton";

import { StreamingState } from "lib/consts/streaming";
import {
    isArmCameraStream,
    isFrontCameraStream,
    isGimbalCameraStream,
    isThermalCameraStream,
    streamDisplayLabel,
} from "lib/utils/connection";

import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";
import { ReactComponent as PinIcon } from "assets/svgs/streaming/pin-stream.svg";

/** Front / gimbal / thermal / arm cam may be pinned when not the main active stream. */
const isPinnableCameraStream = (streamName) =>
    isFrontCameraStream(streamName) ||
    isGimbalCameraStream(streamName) ||
    isThermalCameraStream(streamName) ||
    isArmCameraStream(streamName);

const StreamingSourcePanel = ({ visible = false, className = "" }) => {
    const {
        setShowStreamingSource,
        availableStreams,
        streamDisplayNames,
        streamStatuses,
        activeStream,
        setActiveStream,
        pinnedPayloadStreams,
        togglePinnedPayloadStream,
    } = useWebRTC();

    const pinnedSet = new Set(pinnedPayloadStreams);

    /** Green when healthy; yellow when slow; hide the dot when unavailable (no red). */
    const getStatusColor = (streamName) => {
        const status = streamStatuses.get(streamName);
        switch (status) {
            case StreamingState.Okay:
                return "bg-primary2";
            case StreamingState.Slow:
                return "bg-yellow-500";
            default:
                return null;
        }
    };

    return visible ? (
        <div
            className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 ${className}`}
            onClick={() => setShowStreamingSource(false)}
        >
            <div
                className="flex h-fit w-96 flex-col justify-center space-y-2 rounded-r10 bg-black90 p-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <p className="h4l">Camera Source</p>
                    <FunctionalButton
                        icon={<CloseIcon className="h-4 w-4" />}
                        className="h-8 w-8 rounded-r5 bg-black70 hover:bg-black60"
                        hoverText="Close"
                        onClick={() => setShowStreamingSource(false)}
                    />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {availableStreams.map((streamName) => {
                        const isActive = streamName === activeStream;
                        const isPinned = pinnedSet.has(streamName);
                        // Never offer pin for the stream already on the main view.
                        const showPin = isPinnableCameraStream(streamName) && !isActive;
                        const statusColor = getStatusColor(streamName);

                        return (
                            <div
                                key={streamName}
                                className={`flex items-center gap-1 rounded-r5 p-1 ${
                                    isActive ? "bg-black60" : "bg-black70"
                                }`}
                            >
                                <button
                                    type="button"
                                    className="min-w-0 flex-1 truncate rounded-r5 px-1 py-1 text-left text-sm text-white hover:bg-black60"
                                    onClick={() => {
                                        setActiveStream(streamName);
                                        setShowStreamingSource(false);
                                    }}
                                >
                                    {streamDisplayLabel(
                                        streamName,
                                        streamDisplayNames?.get?.(streamName),
                                    )}
                                </button>
                                {statusColor ? (
                                    <div
                                        className={`h-2 w-2 shrink-0 rounded-full ${statusColor}`}
                                    />
                                ) : null}
                                {showPin && (
                                    <button
                                        type="button"
                                        aria-label={isPinned ? "Unpin stream" : "Pin stream"}
                                        aria-pressed={isPinned}
                                        title={isPinned ? "Unpin stream" : "Pin stream"}
                                        className={`mr-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-r5 ${
                                            isPinned
                                                ? "bg-primary2 text-black"
                                                : "bg-black60 text-white hover:bg-black50"
                                        }`}
                                        onClick={() => togglePinnedPayloadStream(streamName)}
                                    >
                                        <PinIcon className="h-3.5 w-3.5" aria-hidden />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    ) : null;
};

export default StreamingSourcePanel;
